import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Discover from './pages/Discover';
import MatchDetail from './pages/MatchDetail';
import CreateMatch from './pages/CreateMatch';
import MyMatches from './pages/MyMatches';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import Search from './pages/Search';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import CommunityGuidelines from './pages/CommunityGuidelines';

import './index.css';

// Global notification component
const GlobalNotifications = () => {
  const [toast, setToast] = useState(null);
  const { logout } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for token expiration warning
    const handleTokenExpiring = (e) => {
      const secondsLeft = Math.floor(e.detail.secondsUntilExpiry);
      const minutesLeft = Math.ceil(secondsLeft / 60);
      setToast({
        type: 'warning',
        message: `Your session will expire in ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}. Please save your work.`,
      });
      // Auto-dismiss after 5 seconds
      setTimeout(() => setToast(null), 5000);
    };

    // Listen for session expired
    const handleSessionExpired = () => {
      setToast({
        type: 'error',
        message: 'Your session has expired. Please log in again.',
      });
      logout();
      navigate('/login');
    };

    window.addEventListener('tokenExpiring', handleTokenExpiring);
    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('tokenExpiring', handleTokenExpiring);
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [logout, navigate]);

  if (!toast) return null;

  return (
    <div className={`fixed top-4 right-4 p-4 rounded shadow-lg text-white z-50 ${
      toast.type === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
    }`}>
      {toast.message}
    </div>
  );
};

const RootRoute = () => {
  const { isAuthenticated, loading } = useAuthContext();
  
  if (loading) return null;
  
  if (isAuthenticated) {
    return <Dashboard />;
  }
  
  return <Navigate to="/landing" replace />;
};

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <SocketProvider>
          <Router>
            <GlobalNotifications />
            <Routes>
              {/* Root / Dashboard */}
              <Route path="/" element={<RootRoute />} />
              {/* Public — accessible to everyone */}
              <Route path="/landing" element={<Landing />} />
              {/* Protected — require authentication */}
              <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
              <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
              <Route path="/match/:matchId" element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
              <Route path="/profile/:id" element={<ProtectedRoute><PublicProfile /></ProtectedRoute>} />
              
              {/* Public Info Pages */}
              <Route path="/faq" element={<HelpCenter />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/guidelines" element={<CommunityGuidelines />} />


              {/* Public-only — redirect authenticated users away */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />

              {/* Protected — require authentication */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/matches" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/create-match" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
              <Route path="/my-matches" element={<ProtectedRoute><MyMatches /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            </Routes>
          </Router>
        </SocketProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
